import { CategoryId, ProductId } from "shared/hubrise/ids"
import { ICatalog, ICategory, ICategoryOrProduct, IProduct, isProduct } from "shared/hubrise/models"
import * as Sku from "shared/hubrise/utils/Sku"
import {
  findById,
  getIds,
  insertItemInArray,
  moveItemWithinArray,
  removeInArrayById,
  replaceInArrayById,
  replaceItemInArray,
} from "shared/utils/javascript"

import { updatedCatalog } from "./Catalog"
import * as Category from "./Category"
import * as Deal from "./Deal"

/**
 * Add a product.
 */
export function add(
  catalog: ICatalog,
  insertAfter: ICategoryOrProduct,
  values: Partial<IProduct>,
): { newCatalog: ICatalog; product: IProduct } {
  const position: number = isProduct(insertAfter)
    ? catalog.data.products.findIndex(({ id }) => id === insertAfter.id) + 1
    : catalog.data.products.findIndex(({ categoryId }) => categoryId === insertAfter.id) // TODO: does not work for empty categories
  const defaultValues: IProduct = {
    // Keep the order of the properties same as the interface for objectHash to generate consistent hashes.
    id: ProductId(catalog.idGenerator()),
    categoryId: isProduct(insertAfter) ? insertAfter.categoryId : insertAfter.id,
    name: "",
    description: "",
    tags: [],
    legacyAllergenTags: [],
    nutrition: null,
    taxRate: null,
    imageHrIds: [],
    skus: [Sku.create(catalog)],
  }
  const product: IProduct = { ...defaultValues, ...values }
  const products: Array<IProduct> = insertItemInArray(catalog.data.products, product, position)
  const newCatalog: ICatalog = updatedCatalog(catalog, { products })
  return { newCatalog, product }
}

/**
 * Return the product matching the id.
 * @param catalog
 * @param id
 */
export const byId = (catalog: ICatalog, id: ProductId): IProduct => {
  const product = findById(catalog.data.products, id)
  if (!product) throw `Product ${id} not found`
  return product
}

/**
 * Remove all skus but the first one, and delete the remaining sku's name.
 * @param catalog
 * @param product
 */
export function changeToMonosku(catalog: ICatalog, product: IProduct): ICatalog {
  let newCatalog = catalog
  const [keepSku, ...otherSkus] = product.skus
  otherSkus.forEach((sku) => {
    newCatalog = Sku.doDelete(newCatalog, product.id, sku.id)
  })
  newCatalog = update(newCatalog, product.id, "skus", [keepSku])
  newCatalog = Sku.update(newCatalog, product.id, keepSku.id, "name", "")
  return newCatalog
}

/**
 * Delete the product. The function name is because delete is a reserved JS word.
 * @param catalog
 * @param product
 */
export function doDelete(catalog: ICatalog, product: IProduct): ICatalog {
  return updatedCatalog(catalog, {
    products: removeInArrayById(catalog.data.products, product.id),
    deals: Deal.deleteSkuIds(catalog, getIds(product.skus)),
  })
}

/**
 * Return the list of products belonging to the category.
 * @param catalog
 * @param category
 */
export const inCategory = (catalog: ICatalog, category: ICategory): Array<IProduct> =>
  catalog.data.products.filter((product) => product.categoryId === category.id)

/**
 * Returns true if the product has at least one visible SKU in <Preview>.
 * @param product
 * @param variantRef
 */
export const isVisibleInPreview = (product: IProduct, variantRef: string | null): boolean =>
  product.skus.some((sku) => Sku.isVisibleInPreview(sku, variantRef))

/**
 * Returns true if the product is multi sku.
 * @param product
 */
export const isMultiSku = (product: IProduct): boolean => product.skus.length > 1 || product.skus[0].name !== ""

/**
 * Move the product to a new position.
 * @param catalog
 * @param product
 * @param newCategoryId
 * @param insertAfterProductId The product after which the product must be inserted, or null to make the product the first child of the category.
 */
export function move(
  catalog: ICatalog,
  product: IProduct,
  newCategoryId: CategoryId,
  insertAfterProductId: ProductId | null,
): ICatalog {
  const products = removeInArrayById(catalog.data.products, product.id)

  let newPosition: number
  if (insertAfterProductId !== null) {
    newPosition = products.findIndex((p) => p.id === insertAfterProductId) + 1
  } else {
    newPosition = products.findIndex((p) => p.categoryId === newCategoryId) // -1 if new category is empty
  }

  let newProducts: Array<IProduct>
  if (newPosition >= 0) {
    newProducts = moveItemWithinArray(catalog.data.products, product, newPosition, {
      ...product,
      categoryId: newCategoryId,
    })
  } else {
    // New category is empty
    newProducts = stableSortProductsByCategory(
      catalog.data.categories,
      replaceItemInArray(catalog.data.products, product, { ...product, categoryId: newCategoryId }),
    )
  }

  return updatedCatalog(catalog, { products: newProducts })
}

/**
 * Return categories and products, in the order of a depth-first run through.
 * @param catalog
 */
export const sortedCategoriesAndProducts = (catalog: ICatalog): Array<ICategoryOrProduct> => {
  const sortRecursiveInCategory = (category: ICategory): Array<ICategoryOrProduct> => {
    return [
      category,
      ...inCategory(catalog, category),
      ...Category.children(catalog, category).flatMap((child) => sortRecursiveInCategory(child)),
    ]
  }
  return Category.topLevel(catalog).flatMap((category) => sortRecursiveInCategory(category))
}

/**
 * Rearrange products in the same order as their parent categories. Stable sorting is guaranteed in ES2019+.
 * @param categories
 * @param products
 */
export function stableSortProductsByCategory(categories: Array<ICategory>, products: Array<IProduct>): Array<IProduct> {
  const categoryIds = categories.map((c) => c.id)
  return products.sort((a, b) => categoryIds.indexOf(a.categoryId) - categoryIds.indexOf(b.categoryId))
}

/**
 * Update a product and return the resulting catalog.
 * @param catalog
 * @param productId
 * @param fieldName
 * @param value
 */
export function update<T extends keyof IProduct>(
  catalog: ICatalog,
  productId: ProductId,
  fieldName: T,
  value: IProduct[T],
): ICatalog {
  return updatedCatalog(catalog, {
    products: replaceInArrayById(catalog.data.products, productId, (product) => ({
      ...product,
      [fieldName]: value,
    })),
  })
}

/**
 * Update a field to the same value for a list of products.
 * @param catalog
 * @param productIds
 * @param fieldName
 * @param value
 */
export function bulkUpdate<T extends keyof IProduct>(
  catalog: ICatalog,
  productIds: Array<ProductId>,
  fieldName: T,
  value: IProduct[T],
): ICatalog {
  return updatedCatalog(catalog, {
    products: catalog.data.products.map((product) => {
      return productIds.includes(product.id) ? { ...product, [fieldName]: value } : product
    }),
  })
}
